/**
 * EnhancedVideoInput Component
 *
 * Tab-based video input: Upload or Record.
 * All shadcn Tabs/Button replaced with Tailwind.
 * Mode: Deep Analysis (Mode 3)
 */
import React, { useState } from 'react';
import { Upload, Video, Camera } from 'lucide-react';
import { VideoPreview } from './VideoPreview';
import { MovementCameraRecorder } from './MovementCameraRecorder';

export const EnhancedVideoInput = ({ uploadedVideo, setUploadedVideo, setAnalysis }) => {
  const [activeTab, setActiveTab] = useState('upload');
  const [isDragOver, setIsDragOver] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const validateVideoFile = (file) => {
    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
    if (!validTypes.includes(file.type)) {
      setValidationError('Please upload an MP4, MOV, AVI, or WebM video file');
      return false;
    }
    const MAX_SIZE = 20 * 1024 * 1024; // 20MB
    if (file.size > MAX_SIZE) {
      setValidationError('Video file size must be less than 20MB');
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleFileSelect = (file) => {
    if (validateVideoFile(file)) {
      setUploadedVideo(file);
      setAnalysis(null);
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) handleFileSelect(files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) handleFileSelect(files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleCameraRecording = (file) => {
    setUploadedVideo(file);
    setAnalysis(null);
    setCameraOpen(false);
  };

  const clearVideo = () => {
    setUploadedVideo(null);
    setAnalysis(null);
    setValidationError(null);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-zinc-100 mb-2">1. Upload or Record Your Technique Video</h3>

      {validationError && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {validationError}
        </div>
      )}

      {uploadedVideo ? (
        /* Video Preview Mode */
        <div className="space-y-4">
          <VideoPreview file={uploadedVideo} />
          <div className="flex gap-2">
            <button
              onClick={() => { clearVideo(); setActiveTab('upload'); }}
              className="flex-1 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2 text-sm text-zinc-200 hover:bg-white/[0.08] flex items-center justify-center gap-2"
            >
              <Upload className="h-4 w-4" /> Change Video
            </button>
            <button
              onClick={() => setCameraOpen(true)}
              className="flex-1 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2 text-sm text-zinc-200 hover:bg-white/[0.08] flex items-center justify-center gap-2"
            >
              <Camera className="h-4 w-4" /> Record New
            </button>
            <button
              onClick={clearVideo}
              className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-400 hover:bg-red-500/20"
            >
              Clear
            </button>
          </div>
        </div>
      ) : (
        /* Input Selection Mode - Tab UI */
        <div className="w-full">
          {/* Tab Buttons */}
          <div className="grid grid-cols-2 bg-[#0b1118] rounded-xl p-1 gap-1">
            <button
              onClick={() => setActiveTab('upload')}
              className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm transition-colors ${
                activeTab === 'upload'
                  ? 'bg-white/10 text-zinc-100'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <Upload className="h-4 w-4" /> Upload Video
            </button>
            <button
              onClick={() => setActiveTab('record')}
              className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm transition-colors ${
                activeTab === 'record'
                  ? 'bg-white/10 text-zinc-100'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <Video className="h-4 w-4" /> Record Video
            </button>
          </div>

          {/* Tab Content */}
          <div className="mt-4">
            {activeTab === 'upload' && (
              <div
                className={`bg-[#0b1118] border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer group ${
                  isDragOver
                    ? 'border-orange-400 bg-orange-400/5'
                    : 'border-white/15 hover:border-white/30'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => document.getElementById('video-upload')?.click()}
              >
                <input
                  id="video-upload"
                  type="file"
                  accept="video/mp4,video/quicktime,video/x-msvideo,video/webm"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className={`text-orange-400 transition-transform group-hover:scale-110 ${isDragOver ? 'scale-110' : ''}`}>
                    <Upload className="h-12 w-12" />
                  </div>
                  <div>
                    <p className="text-zinc-200 font-medium">
                      {isDragOver ? 'Drop your video here' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-sm text-zinc-500 mt-1">
                      MP4, MOV, AVI, or WebM (max 20MB)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'record' && (
              <div className="bg-[#0b1118] border border-white/10 rounded-xl p-8 text-center">
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className="text-orange-400">
                    <Camera className="h-12 w-12" />
                  </div>
                  <div>
                    <p className="text-zinc-200 font-medium mb-2">
                      Record your movement technique
                    </p>
                    <p className="text-sm text-zinc-500 mb-4">
                      Use your device camera to record a short video for analysis
                    </p>
                    <button
                      onClick={() => setCameraOpen(true)}
                      className="rounded-xl bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 text-sm flex items-center gap-2 mx-auto"
                    >
                      <Video className="h-4 w-4" /> Start Recording
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <MovementCameraRecorder
        isOpen={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onVideoRecorded={handleCameraRecording}
      />
    </div>
  );
};
